import React, { useState } from 'react';

const TrialPage: React.FC = () => {
  const [formData, setFormData] = useState({
    name: '',
    company: '',
    email: '',
    phone: '',
    employees: '',
    industry: '',
    concerns: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    try {
      const formDataToSend = new FormData();
      Object.entries(formData).forEach(([key, value]) => {
        formDataToSend.append(key, value);
      });

      const response = await fetch('https://readdy.ai/api/form/trial-consultation', {
        method: 'POST',
        body: new URLSearchParams(formDataToSend as any)
      });

      if (response.ok) {
        setSubmitStatus('success');
        setFormData({
          name: '',
          company: '',
          email: '',
          phone: '',
          employees: '',
          industry: '',
          concerns: '',
          message: ''
        });
      } else {
        setSubmitStatus('error');
      }
    } catch (error) {
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-orange-600 via-orange-500 to-orange-400 text-white overflow-hidden">
        <div 
          className="absolute inset-0 bg-cover bg-center bg-no-repeat opacity-40"
          style={{
            backgroundImage: `url('https://public.readdy.ai/ai/img_res/e87e19b7ff12da02c41a152f464f3cf7.jpg')`,
          }}
        />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
          <div className="text-center space-y-8">
            <h1 className="text-5xl lg:text-6xl font-black leading-tight tracking-tight">
              まずは試してみませんか？<br />
              <span className="text-orange-200 font-extrabold">2ヶ月間の試し顧問制度</span>
            </h1>
            <p className="text-2xl text-orange-100 leading-relaxed font-medium max-w-4xl mx-auto">
              正規料金の<span className="font-bold text-3xl text-white bg-orange-600 px-3 py-1 rounded">60%</span>で<br />
              社外財務部長サービスを体験していただけます
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a 
                href="#trial-form" 
                className="bg-white text-orange-600 px-8 py-4 rounded-lg font-bold text-xl transition-colors whitespace-nowrap cursor-pointer shadow-lg hover:shadow-xl transform hover:scale-105"
              >
                📝 試し顧問に申し込む
              </a>
              <a 
                href="/" 
                className="border-2 border-white text-white hover:bg-white hover:text-orange-600 px-8 py-4 rounded-lg font-bold text-xl transition-colors whitespace-nowrap cursor-pointer shadow-lg hover:shadow-xl transform hover:scale-105"
              >
                サービス詳細を見る
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Trial Benefits Section */}
      <section className="py-16 bg-orange-50">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-4xl font-black text-center text-gray-900 mb-12 tracking-tight">
            2ヶ月試し顧問の<span className="text-orange-600">3つのメリット</span>
          </h2>
          
          <div className="grid md:grid-cols-3 gap-8">
            <div className="bg-white p-8 rounded-lg shadow-xl border-t-4 border-orange-500 hover:shadow-2xl transition-shadow">
              <div className="text-center mb-6">
                <i className="ri-money-dollar-circle-line text-orange-500 text-6xl w-16 h-16 flex items-center justify-center mx-auto mb-4"></i>
                <h3 className="text-2xl font-bold text-gray-900 mb-4">低リスクでスタート</h3>
              </div>
              <p className="text-gray-700 leading-relaxed text-lg font-medium">
                正規料金の<span className="font-bold text-orange-600">60%</span>という特別価格で、まずは2ヶ月間お試しいただけます。大きな投資をする前に、効果を実感していただけます。
              </p>
            </div>
            
            <div className="bg-white p-8 rounded-lg shadow-xl border-t-4 border-blue-500 hover:shadow-2xl transition-shadow">
              <div className="text-center mb-6">
                <i className="ri-time-line text-blue-500 text-6xl w-16 h-16 flex items-center justify-center mx-auto mb-4"></i>
                <h3 className="text-2xl font-bold text-gray-900 mb-4">短期間で効果実感</h3>
              </div>
              <p className="text-gray-700 leading-relaxed text-lg font-medium">
                2ヶ月という短期間でも、<span className="font-bold text-blue-600">経営の見える化</span>や<span className="font-bold text-green-600">PDCAサイクルの構築</span>など、具体的な効果を実感していただけます。
              </p>
            </div>
            
            <div className="bg-white p-8 rounded-lg shadow-xl border-t-4 border-green-500 hover:shadow-2xl transition-shadow">
              <div className="text-center mb-6">
                <i className="ri-shield-check-line text-green-500 text-6xl w-16 h-16 flex items-center justify-center mx-auto mb-4"></i>
                <h3 className="text-2xl font-bold text-gray-900 mb-4">安心の判断期間</h3>
              </div>
              <p className="text-gray-700 leading-relaxed text-lg font-medium">
                2ヶ月間で<span className="font-bold text-green-600">相性や効果</span>をじっくりご判断いただけます。継続するかどうかは、効果を実感してからお決めください。
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Trial Process Section */}
      <section className="py-16 bg-white">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-4xl font-black text-center text-gray-900 mb-12 tracking-tight">
            試し顧問の<span className="text-blue-600">進め方</span>
          </h2>
          
          <div className="grid md:grid-cols-4 gap-8">
            <div className="text-center">
              <div className="bg-orange-100 rounded-full w-20 h-20 flex items-center justify-center mx-auto mb-6">
                <span className="text-3xl font-black text-orange-600">1</span>
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-4">お申し込み</h3>
              <p className="text-gray-700 leading-relaxed font-medium">
                下記フォームからお申し込みください。24時間以内にご連絡いたします。
              </p>
            </div>
            
            <div className="text-center">
              <div className="bg-blue-100 rounded-full w-20 h-20 flex items-center justify-center mx-auto mb-6">
                <span className="text-3xl font-black text-blue-600">2</span>
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-4">初回面談</h3>
              <p className="text-gray-700 leading-relaxed font-medium">
                現状の課題やご要望をお聞きし、最適なサポート方法をご提案します。
              </p>
            </div>
            
            <div className="text-center">
              <div className="bg-green-100 rounded-full w-20 h-20 flex items-center justify-center mx-auto mb-6">
                <span className="text-3xl font-black text-green-600">3</span>
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-4">2ヶ月実施</h3>
              <p className="text-gray-700 leading-relaxed font-medium">
                月1〜2回の訪問で、実際の経営サポートを体験していただきます。
              </p>
            </div>
            
            <div className="text-center">
              <div className="bg-purple-100 rounded-full w-20 h-20 flex items-center justify-center mx-auto mb-6">
                <span className="text-3xl font-black text-purple-600">4</span>
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-4">継続判断</h3>
              <p className="text-gray-700 leading-relaxed font-medium">
                効果を実感いただけましたら、正規契約への移行をご検討ください。
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Trial Form Section */}
      <section id="trial-form" className="py-16 bg-gray-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-black text-gray-900 mb-6 tracking-tight">
              2ヶ月試し顧問<span className="text-orange-600">お申し込みフォーム</span>
            </h2>
            <p className="text-xl text-gray-700 leading-relaxed font-medium">
              まずはお気軽にお申し込みください。24時間以内にご連絡いたします。
            </p>
          </div>
          
          <div className="bg-white rounded-lg shadow-xl p-8">
            {submitStatus === 'success' && (
              <div className="mb-8 p-6 bg-green-50 border border-green-200 rounded-lg">
                <div className="flex items-center">
                  <i className="ri-check-circle-line text-green-500 text-2xl mr-3"></i>
                  <p className="text-green-700 font-semibold text-lg">
                    お申し込みありがとうございます。24時間以内にご連絡いたします。
                  </p>
                </div>
              </div>
            )}
            
            {submitStatus === 'error' && (
              <div className="mb-8 p-6 bg-red-50 border border-red-200 rounded-lg">
                <div className="flex items-center">
                  <i className="ri-error-warning-line text-red-500 text-2xl mr-3"></i>
                  <p className="text-red-700 font-semibold text-lg">
                    送信に失敗しました。もう一度お試しください。
                  </p>
                </div>
              </div>
            )}
            
            <form onSubmit={handleSubmit} className="space-y-6" data-readdy-form id="trial-form">
              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <label htmlFor="name" className="block text-lg font-semibold text-gray-700 mb-2">
                    お名前 <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    value={formData.name}
                    onChange={handleInputChange}
                    required
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent text-lg"
                    placeholder="山田太郎"
                  />
                </div>
                
                <div>
                  <label htmlFor="company" className="block text-lg font-semibold text-gray-700 mb-2">
                    会社名 <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    id="company"
                    name="company"
                    value={formData.company}
                    onChange={handleInputChange}
                    required
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent text-lg"
                    placeholder="株式会社○○"
                  />
                </div>
              </div>
              
              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <label htmlFor="email" className="block text-lg font-semibold text-gray-700 mb-2">
                    メールアドレス <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    required
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent text-lg"
                    placeholder="example@company.com"
                  />
                </div>
                
                <div>
                  <label htmlFor="phone" className="block text-lg font-semibold text-gray-700 mb-2">
                    電話番号 <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="tel"
                    id="phone"
                    name="phone"
                    value={formData.phone}
                    onChange={handleInputChange}
                    required
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent text-lg"
                    placeholder="03-1234-5678"
                  />
                </div>
              </div>
              
              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <label htmlFor="employees" className="block text-lg font-semibold text-gray-700 mb-2">
                    従業員数 <span className="text-red-500">*</span>
                  </label>
                  <select
                    id="employees"
                    name="employees"
                    value={formData.employees}
                    onChange={handleInputChange}
                    required
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent text-lg pr-8"
                  >
                    <option value="">選択してください</option>
                    <option value="1-5人">1-5人</option>
                    <option value="6-10人">6-10人</option>
                    <option value="11-20人">11-20人</option>
                    <option value="21-30人">21-30人</option>
                    <option value="31人以上">31人以上</option>
                  </select>
                </div>
                
                <div>
                  <label htmlFor="industry" className="block text-lg font-semibold text-gray-700 mb-2">
                    業種 <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    id="industry"
                    name="industry"
                    value={formData.industry}
                    onChange={handleInputChange}
                    required
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent text-lg"
                    placeholder="製造業、小売業、サービス業など"
                  />
                </div>
              </div>
              
              <div>
                <label htmlFor="concerns" className="block text-lg font-semibold text-gray-700 mb-2">
                  現在の経営課題 <span className="text-red-500">*</span>
                </label>
                <textarea
                  id="concerns"
                  name="concerns"
                  value={formData.concerns}
                  onChange={handleInputChange}
                  required
                  rows={4}
                  maxLength={500}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent text-lg resize-none"
                  placeholder="お金の流れが見えない、社員との温度差、経営に集中できないなど、現在お困りのことをお聞かせください（500文字以内）"
                />
                <p className="text-sm text-gray-500 mt-1">{formData.concerns.length}/500文字</p>
              </div>
              
              <div>
                <label htmlFor="message" className="block text-lg font-semibold text-gray-700 mb-2">
                  その他ご質問・ご要望
                </label>
                <textarea
                  id="message"
                  name="message"
                  value={formData.message}
                  onChange={handleInputChange}
                  rows={3}
                  maxLength={500}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent text-lg resize-none"
                  placeholder="ご質問やご要望がございましたらお聞かせください（500文字以内）"
                />
                <p className="text-sm text-gray-500 mt-1">{formData.message.length}/500文字</p>
              </div>
              
              <div className="text-center">
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="bg-orange-500 hover:bg-orange-600 disabled:bg-gray-400 text-white px-12 py-4 rounded-lg font-bold text-xl transition-colors whitespace-nowrap cursor-pointer shadow-lg hover:shadow-xl transform hover:scale-105 disabled:transform-none"
                >
                  {isSubmitting ? '送信中...' : '📝 試し顧問に申し込む'}
                </button>
              </div>
            </form>
          </div>
        </div>
      </section>
    </div>
  );
};

export default TrialPage;